import axiosInstance from 'src/config/axios.config';

// ----------------------------------------------------------------------
// ADMIN/SUPER_ADMIN device-version reporting (logs/device-versions task). This is the FIXED
// contract the FE builds against; the backend endpoints (`/api/admin/devices/versions`) do not
// exist in THIS repo yet — they integrate at deploy. The authoritative gate is the server's
// `@PreAuthorize`; the FE `RequireRole` guard is UX only. Mirrors the errorLogs.api.ts pattern.
//
// `getEmployeeDeviceInfo` below hits the EXISTING `/api/employees/{id}/device-info` endpoint (not a
// new contract) — added here since no typed fetch fn existed yet for it.
// ----------------------------------------------------------------------

export interface EmployeeDeviceVersionDto {
  employeeId: string;
  employeeName: string;
  email: string;
  branchId: string | null;
  branchName: string | null;
  hasActiveDevice: boolean;
  appVersion: string | null;
  osType: string | null;
  osVersion: string | null;
  deviceModel: string | null;
  lastUsedAt: string | null;
  appVersionUpdatedAt: string | null;
  latestAppVersion: string | null;
  upToDate: boolean | null;
}

export interface PagedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

// Filter set is the fixed contract: branchId,upToDate,q.
export interface DeviceVersionFilters {
  branchId?: string;
  upToDate?: boolean;
  q?: string;
}

const BASE = '/api/admin/devices/versions';

export const deviceVersionsApi = {
  // GET /api/admin/devices/versions — paged/filtered.
  async list(
    filters: DeviceVersionFilters = {},
    page = 0,
    size = 25
  ): Promise<PagedResponse<EmployeeDeviceVersionDto>> {
    const response = await axiosInstance.get<PagedResponse<EmployeeDeviceVersionDto>>(BASE, {
      params: {
        page,
        size,
        branchId: filters.branchId || undefined,
        upToDate: filters.upToDate ?? undefined,
        q: filters.q || undefined,
      },
    });
    return response.data;
  },
};

// ----------------------------------------------------------------------
// Display helper (kept in the API layer so the visual layer can be reskinned freely).
// ----------------------------------------------------------------------

export type UpToDateColor = 'success' | 'error' | 'default';

export function upToDateLabel(upToDate: boolean | null | undefined): {
  label: string;
  color: UpToDateColor;
} {
  if (upToDate === true) return { label: 'Güncel', color: 'success' };
  if (upToDate === false) return { label: 'Eski Sürüm', color: 'error' };
  return { label: 'Cihaz Yok', color: 'default' };
}

// ----------------------------------------------------------------------
// device-info — EXISTING endpoint `GET /api/employees/{id}/device-info` (not a new contract).
// ----------------------------------------------------------------------

export interface EmployeeDeviceInfoDto {
  hasActiveDevice: boolean;
  deviceId?: string;
  deviceModel?: string;
  osType?: string;
  osVersion?: string;
  appVersion?: string;
  appVersionUpdatedAt?: string;
  latestAppVersion?: string;
  upToDate?: boolean | null;
  registeredAt?: string;
  lastUsedAt?: string;
}

export async function getEmployeeDeviceInfo(id: string): Promise<EmployeeDeviceInfoDto> {
  const response = await axiosInstance.get<EmployeeDeviceInfoDto>(
    `/api/employees/${id}/device-info`
  );
  return response.data;
}
